import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Back from 'shared-vectors/icons/ModalBack';
import Close from 'shared-vectors/icons/MapToolsClose';
import Localization from 'shared-vectors/icons/MapLocalization';
import LocalizationIos from 'shared-vectors/icons/MapLocalizationIos';
import CloseCirc from 'shared-vectors/icons/CloseCirc';
import MagnifyingGlass from 'shared-vectors/icons/MagnifyingGlass';
import Walk from 'shared-vectors/icons/NearbyWalk';
import Bicycle from 'shared-vectors/icons/NearbyBike';
import Car from 'shared-vectors/icons/NearbyCar';
import { Icon } from 'shared-vectors';

import computeSearchWhere from 'shared-utils/src/computeSearchWhere';

import Grid from 'shared-components/components/Grid';
import GridItem from 'shared-components/components/GridItem';

import {
  DRAWING_ACTIONS_MAP,
  DRAWING_ACTIONS_COPY,
  TRAVEL_COPY_MAP,
  animated,
  Arrow,
} from '../../../../constants';

import getAddress from '../../../../helpers/getAddress';
import decorateMapLocality from '../../../../helpers/decorateMapLocality';

import QbBtn from '../../../layout/QbBtn';
import Buttons from '../../../layout/Buttons';
import GeoLocationBtn from '../../../layout/GeoLocationBtn';
import GoogleSearch from '../../GoogleSearch';

const Controls = ({
  map,
  searchMode,
  setSearchMode,
  mobileOs,
  device,
  libraries,
  hasDrawing,
  center,
  drawingData,
  setDrawingDataAddress,
  setNearbyCenter,
  availableResults,
  availablePoi,
  updateFiltersToAddToMapQuery,
  confirmNearby,
  travelTime,
  setTravelTime,
  travelMode,
  setTravelMode,
  polygonPath,
  deletePolygon,
  revertNearby,
  deleteNearbyCenter,
  viewportWidth,
  openQbFiltersPanel,
  qbGoToSRP,
  qbSSearchAction,
  controlsVariant,
  isShowSaveAddressDialogOpen,
  showSaveAddressDialog,
  isHandleAddressesModalOpen,
  showHandleAddressesModal,
  updateSearchAddressRef,
}) => {
  const gSearchTrigger = useRef(null);
  const [isGSearchOn, setIsGSearchOn] = useState(false);
  const [searchAddress, setSearchAddress] = useState(null);
  const [gSearchError, setGSearchError] = useState('');

  const updateSearchAddress = (addr) => {
    setSearchAddress(addr);
    updateSearchAddressRef(addr);
  };

  useEffect(() => {
    if (searchMode) {
      updateSearchAddress(drawingData?.nearbySearchAddress || null);
    }
  }, [searchMode]);

  const activateGoogleSearch = () => {
    gSearchError && setGSearchError('');
    setIsGSearchOn(true);
  };

  const erase = (e) => {    
    e.stopPropagation();
    if (polygonPath) {
      deletePolygon();
    } else {
      deleteNearbyCenter();
    }
    updateSearchAddress(null);
  };

  const close = () => {
    if (drawingData && drawingData.action === DRAWING_ACTIONS_MAP.nearby) {
      revertNearby();
    } else if (hasDrawing) {
      deletePolygon();
      updateSearchAddress(null);
      setDrawingDataAddress(null);
      setSearchMode(null);
    } else {
      deleteNearbyCenter();
      setSearchMode(null);
      setDrawingDataAddress(null);
    }
    isGSearchOn && setIsGSearchOn(false);
  };

  const isValidSearch = (travelTime && travelMode);

  const shapeFilter = center && !center.delete && isValidSearch ? {
    nearby: {
      location: { lat: center.lat(), lon: center.lng() },
      travel_mode: travelMode,
      travel_time: parseInt(travelTime, 10),
    },
    q: null,
    geobounds: null,
    geopolygon: null,
    geocircle: null,
  } : {};

  const startSearch = () => {
    if (controlsVariant === 'only-confirm-ssearch') {
      qbSSearchAction({
        mapLocations: decorateMapLocality({}),
        mapFilters: shapeFilter,
        mapDrawingData: {
          action: DRAWING_ACTIONS_MAP.nearby,
          travelMode,
          travelTime: parseInt(travelTime, 10),
          nearbyCenter: { lat: center.lat(), lon: center.lng() },
          nearbyPolygon: humanPPath,
          nearbySearchAddress: searchAddress,
          fromSSearch: true,
        },
      });
    } else if (qbGoToSRP) {
      qbGoToSRP({
        tempLocations: computeSearchWhere(
          decorateMapLocality({
            drawingData: {
              nearbyCenter: true,
              travelMode,
              travelTime: parseInt(travelTime, 10),
            },
            searchAddress,
          }), []),
        shapeFilter,
        forceMap: true,
      });
    } else {
      confirmNearby({ 
        path: polygonPath, 
        nearbySearchAddress: searchAddress, 
        ar: availableResults,
        clickFrom: 'QB_MainButton',
      });
    }
  };

  const resetGSearchError = () => setGSearchError('');

  const gSearchAction = ({
    name,
    parent,
    location,
    error,
  }) => {
    if (error) {
      setGSearchError(<>Non è possibile trovare la tua posizione al momento.<br />Riprova o usa la barra di ricerca.</>);
    } else {
      const { core: { LatLng } } = libraries;    
      if (hasDrawing) {
        deletePolygon();
      }

      setNearbyCenter({ center: new LatLng({ lat: location.lat, lng: location.lng }) });
      updateSearchAddress({ name, parent });
    }
  };

  useEffect(() => {
    const getAddressFromCircleCenter = async (coords) => {
      const { geocoding: { Geocoder } } = libraries;
      const service = new Geocoder();
      const data = await service.geocode({ location: coords });
      const { results } = data;
      const [place] = results;
      const { address_components, geometry: { location, viewport } } = place;

      const addressComponents = address_components.map(ac => ({
        shortText: ac.short_name,
        longText: ac.long_name,
        types: ac.types,
      }));

      const address = getAddress(addressComponents);  
      const { num, addr, loc } = address;

      const resultObj = {
        name: `${addr}${num ? ` ${num}` : ''}`,
        parent: loc,
        location: JSON.parse(JSON.stringify(location)),
        viewport: JSON.parse(JSON.stringify(viewport)),
      };

      updateSearchAddress({ name: resultObj.name, parent: resultObj.parent, number: num });
      if (drawingData && !drawingData.nearbySearchAddress) {
        setDrawingDataAddress({ name: resultObj.name, parent: resultObj.parent });
      }
    };    

    if (center) {
      if (center.delete) {
        updateSearchAddress(null);
      } else {
        getAddressFromCircleCenter(center);
      }
    }
  }, [center]);


  let searchButtonCont = 'Nessun risultato';
  if (isValidSearch) {
    if (availableResults?.loading) {
      searchButtonCont = animated();
    } else if (availableResults?.value) {
      searchButtonCont = controlsVariant
        ? <>Conferma (<span className="tp-w--l">{availableResults.value}</span> risultati)</>
        : `Vedi ${availableResults.value} risultati`;
    }
  }

  const actAsSmartphone = viewportWidth < 1024;

  const canSearch = isValidSearch && polygonPath && !availableResults?.loading;

  const closeOnEnter = (e) => {
    if (e.key === 'Enter') {
      close();
    }
  };

  const activateGoogleSearchOnEnter = (e) => {
    if (e.key === 'Enter') {
      activateGoogleSearch();
    }
  };

  const eraseOnEnter = (e) => {
    if (e.key === 'Enter') {
      erase();
    }
  };  

  const startSearchOnEnter = (e) => {
    if (e.key === 'Enter' && canSearch) {
      startSearch();
    }
  };

  return (
    <>      
      <div className="csa_mapsearch__bod__over__layer__top">
        <div className="csa_mapsearch__bod__over__layer__top__hdr c-bg--w mb--s">
          <div
            role="button"
            tabIndex={0}
            className="csa_mapsearch__bod__over__layer__top__hdr__close c-txt--secondary is-clickable"
            onClick={close}
            onKeyDown={closeOnEnter}
            aria-label="Chiudi"
          >
            <Icon aria-hidden="true" name={actAsSmartphone ? Back : Close} width={actAsSmartphone ? null : '16px'} fill />
          </div>
          <img
            aria-hidden="true"
            className="csa_mapsearch__bod__over__layer__top__hdr__icon"
            src="https://www.casa.it/external-resources/map/nearby.svg"
            alt={DRAWING_ACTIONS_COPY.nearby.desktop}
            width="100"
            height="80"
          />
          <div className="csa_mapsearch__bod__over__layer__top__hdr__claim">
            <p className="csa_mapsearch__bod__over__layer__top__hdr__claim--fst tp-s--l tp-w--l c-txt--f0">{DRAWING_ACTIONS_COPY.nearby.mobile}</p>
            <p className="csa_mapsearch__bod__over__layer__top__hdr__claim--sec tp-s--m tp-w--s c-txt--f0">{DRAWING_ACTIONS_COPY.nearby.secondary}</p>
          </div>
        </div>
        <div
          ref={gSearchTrigger}
          role="button"
          tabIndex={0}
          aria-label={searchAddress ? `${searchAddress.name}, ${searchAddress.parent}` : 'Cerca indirizzo di partenza'}
          className={`csa_mapsearch__bod__over__layer__gsearchtrigger pt--s pr--s pb--s pl--s c-bg--w is-clickable${searchAddress ? ' compiled' : ''}`}
          onClick={activateGoogleSearch}
          onKeyDown={activateGoogleSearchOnEnter}
        >
          <GridItem behavior="fixed" className="csa_mapsearch__bod__over__layer__gsearchtrigger__lens">
            <Icon
              aria-hidden="true"
              name={MagnifyingGlass}
              width="24px"
              className="mr--xs"
              fill
            />
          </GridItem>
          <GridItem className={`csa_mapsearch__bod__over__layer__gsearchtrigger__txt tp-s--l tp-w--s c-txt--${searchAddress ? 'f0' : 'f5'} tp--ell`}>{searchAddress ? <>{searchAddress.name}, {searchAddress.parent}</> : 'Cerca indirizzo di partenza'}</GridItem>
          {searchAddress ? (
            <GridItem
              behavior="fixed"
              className="csa_mapsearch__bod__over__layer__gsearchtrigger__btn ml--ms c-txt--f5 is-rel is-clickable"
              onClick={erase}
              onKeyDown={eraseOnEnter}
              role="button"
              tabIndex={0}
              aria-label="Elimina indirizzo"
            >
              <div className="csa_mapsearch__bod__over__layer__gsearchtrigger__btn__click csa_mapsearch__bod__over__layer__gsearchtrigger__btn--erase">
                <Icon aria-hidden="true" name={CloseCirc} />
              </div>
            </GridItem>
          ) : null}
          {(!searchAddress && actAsSmartphone) ? (
            <GeoLocationBtn
              action={gSearchAction}
              className="ml--ms"
              core={libraries?.core}
              geocoding={libraries?.geocoding}
              resetGSearchError={resetGSearchError}
              childrenClassName="csa_mapsearch__bod__over__layer__gsearchtrigger__btn"
            >
              <GridItem behavior="fixed" className="csa_mapsearch__bod__over__layer__gsearchtrigger__btn c-txt--f0 is-clickable">
                <Icon aria-hidden="true" name={mobileOs === 'ios' ? LocalizationIos : Localization} width="24px" fill />
              </GridItem>
            </GeoLocationBtn>
          ) : null}            
        </div>
        {!searchAddress ? (
          <Grid>
            <GridItem behavior="fixed" className="csa_mapsearch__bod__over__layer__searchbaloon p--ms c-bg--secondary is-rel">
              <Arrow className="csa_mapsearch__bod__over__layer__searchbaloon__arr" />
              <p className="tp-s--s c-txt--w">
                Cerca indirizzo o tocca il punto da cui iniziare sulla mappa
              </p>
              {actAsSmartphone ? (
                <>
                  <p className="mt--m tp-s--s c-txt--w">Vuoi cercare intorno a te?</p>
                  <GeoLocationBtn
                    action={gSearchAction}
                    core={libraries?.core}
                    geocoding={libraries?.geocoding}
                    childrenClassName="csa_mapsearch__bod__over__layer__searchbaloon__btn"
                    className="csa_mapsearch__bod__over__layer__searchbaloon__btn mt--s c-bg--w b--s c-br--f0"
                    gSearchError={gSearchError}
                    resetGSearchError={resetGSearchError}
                  >
                    <Grid className="pl--s pt--xs pb--xs" align="center">
                      <GridItem behavior="fixed">
                        <Icon aria-hidden="true" name={mobileOs === 'ios' ? LocalizationIos : Localization} className="mr--xs" width="24px" fill />
                      </GridItem>
                      <GridItem className="tp-s--s tp-w--m c-txt--f0">
                        Usa la tua posizione
                      </GridItem>                        
                    </Grid>

                  </GeoLocationBtn>
                </>
              ) : null}
            </GridItem>
          </Grid>
        ) : null}
        {(!searchAddress && !actAsSmartphone) ? (
          <>
            <p className="csa_mapsearch__bod__over__layer__glbtn--p pt--ms pb--ms tp-s--s c-txt--f0 tp-a--c">oppure</p>
            <GeoLocationBtn
              action={gSearchAction}
              core={libraries?.core}
              geocoding={libraries?.geocoding}
              childrenClassName="csa_mapsearch__bod__over__layer__glbtn"
              className="csa_mapsearch__bod__over__layer__glbtn c-bg--w b--s is-clickable c-txt--f5"
              gSearchError={gSearchError}
              resetGSearchError={resetGSearchError}
            >
              <Grid className="pl--s pt--xs pb--xs pr--ms" align="center">
                <GridItem behavior="fixed">
                  <Icon aria-hidden="true" name={mobileOs === 'ios' ? LocalizationIos : Localization} className="mr--xs" width="24px" fill />
                </GridItem>
                <GridItem className="tp-s--s tp-w--m">
                  Usa la tua posizione
                </GridItem>
              </Grid>
            </GeoLocationBtn>
          </>
        ) : null}
        {isGSearchOn ? (
          <GoogleSearch
            map={map}
            mobileOs={mobileOs}
            device={device}
            places={libraries?.places}
            core={libraries?.core}
            geocoding={libraries?.geocoding}
            searchTrigger={gSearchTrigger.current}
            isSearching={isGSearchOn}
            setIsSearching={setIsGSearchOn}
            className="csa_mapsearch__bod__over__layer__gsearch"
            action={gSearchAction}
            isQBuilder={!!qbSSearchAction || !!qbGoToSRP}
            isShowSaveAddressDialogOpen={isShowSaveAddressDialogOpen}
            showSaveAddressDialog={showSaveAddressDialog}
            isHandleAddressesModalOpen={isHandleAddressesModalOpen}
            showHandleAddressesModal={showHandleAddressesModal}
          />
        ) : null}          
      </div>        
      <div className="csa_mapsearch__bod__over__layer__bot">
        {searchAddress ? (
          <div className="csa_mapsearch__draw__radiusSlider pr--m pb--ms pl--m c-bg--w">
            <p className="csa_mapsearch__draw__radiusSlider__text--nearby tp-s--l tp-w--s tp-a--c">
              {travelMode && travelTime ? (
                <span className="csa_mapsearch__draw__radiusSlider__text--nearby__ell"><span className="tp-w--m">{travelTime}’ {TRAVEL_COPY_MAP[travelMode]}</span> da {searchAddress.name}, {searchAddress.parent}</span>
              ) : (
                <>Scegli mezzo e tempo di percorrenza</>
              )}
              
            </p>
            <Grid direction="column" className="csa_mapsearch__draw__radiusSlider__w100">
              <Buttons
                filter="time"
                className="mb--ms"
                value={travelTime}
                values={[{
                  label: '10 min',
                  value: '10',
                },
                {
                  label: '20 min',
                  value: '20',
                },
                {
                  label: '30 min',
                  value: '30',
                },
                {
                  label: '45 min',
                  value: '45',
                }]}
                update={setTravelTime}
              />
              <Buttons
                filter="mode"
                value={travelMode}
                values={[{
                  label: 'walk',
                  icon: <Icon name={Walk} fill width="26px" height="26px" />,
                  value: 'walk',
                },
                {
                  label: 'bike',
                  icon: <Icon name={Bicycle} fill width="26px" height="26px" />,
                  value: 'bike',
                },
                {
                  label: 'car',
                  icon: <Icon name={Car} fill width="26px" height="26px" />,
                  value: 'car',
                }]}
                update={setTravelMode}
              />
            </Grid>
            <Grid className="csa_mapsearch__bod__over__layer__bot__btns" align="center">
              {!controlsVariant ? (
                <QbBtn
                  searchAddress={searchAddress}
                  availableResults={availableResults}
                  availablePoi={availablePoi}
                  shapeFilter={shapeFilter}
                  updateFiltersToAddToMapQuery={updateFiltersToAddToMapQuery}
                  disabled={!isValidSearch}
                  openQbFiltersPanel={openQbFiltersPanel}
                  qbLocation={decorateMapLocality({
                    drawingData: {
                      nearbyCenter: true,
                      travelMode,
                      travelTime: parseInt(travelTime, 10),
                    },
                    searchAddress,
                  })}
                />
              ) : null}
              <GridItem className="csa_mapsearch__bod__over__layer__bot__btn">
                <div
                  role="button"
                  tabIndex={canSearch ? 0 : -1}
                  className={`csa_mapsearch__bod__over__layer__bot__btn tp-s--l tp-w--l c-txt--w c-bg--primary b--s c-br--primary tp-a--c is-clickable${!canSearch ? ' disabled' : ''}`}
                  onClick={startSearch}
                  onKeyDown={startSearchOnEnter}
                  aria-live="polite"
                >
                  {searchButtonCont}
                </div>
              </GridItem>
            </Grid>
          </div>
        ) : null}
      </div> 
      {!actAsSmartphone ? (
        <>
          <div
            className="csa_mapsearch__bod__over__layer__dskhandle is-clickable"
            onClick={close}
          />
          <Grid className="csa_mapsearch__bod__over__layer__reset" justify="center">
            <GridItem
              behavior="fixed"
              role="button"
              tabIndex={0}
              className="csa_mapsearch__bod__over__layer__reset__btn pr--m pl--m tp-s--l tp-w--m b--s tp-a--c c-txt--secondary is-clickable"
              onClick={close}
              onKeyDown={closeOnEnter}
            >
              Annulla
            </GridItem>
          </Grid>
        </>
      ) : null}
    </>
  );
};

export default Controls;

Controls.propTypes = {
  map: PropTypes.instanceOf(Object),
  searchMode: PropTypes.string,
  setSearchMode: PropTypes.func,
  mobileOs: PropTypes.string,
  device: PropTypes.string,
  libraries: PropTypes.instanceOf(Object),
  viewportWidth: PropTypes.number,
  openQbFiltersPanel: PropTypes.func,
  qbGoToSRP: PropTypes.func,
  qbSSearchAction: PropTypes.func,
  controlsVariant: PropTypes.string,
  updateSearchAddressRef: PropTypes.func,
};

Controls.defaultProps = {
  map: null,
  searchMode: null,
  setSearchMode: null,
  mobileOs: '',
  device: '',
  libraries: null,
  viewportWidth: 0,
  openQbFiltersPanel: null,
  qbGoToSRP: null,
  qbSSearchAction: null,
  controlsVariant: null,
  updateSearchAddressRef: () => {},
};
